import { Button, Col, Row } from "antd";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputNumber, StyledFormInputs, StyledFormSelect } from "../../components/Form/StyledFormInputs";
import SelectCountryCode from "~components/SelectCountryCode/SelectCountryCode.tsx";
import timeZones from "../../config/timeZones";
import { useFetchSites, useFetchVenue } from "../../entities/api";
import { useVenueService } from "~pages/Venues/hooks/useVenueService.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { LabelValue } from "~types/labelValue.ts";
import type { ISite } from "../Sites/types";
import VenuesHistoricNames from "./VenuesHistoricNames";
import { sortStringValues } from "~utils/utilities.ts";
import { noLeadingOrTrailingSpacesReactHookFormAdapter } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.VENUE;

const statuses = ["ACTIVE", "INACTIVE"];

const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;
const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;

interface IFormInput {
  nameLocal: string;
  abbreviationLocal: string;
  nameLatin: string;
  abbreviationLatin: string;
  countryCode: string;
  timezone: string;
  status: string;
  siteId: string;
  capacity: number;
  address: {
    city: string | null;
    state: string | null;
    postalCode: string | null;
    address1: string | null;
    address2: string | null;
    address3: string | null;
    latitude: number | null;
    longitude: number | null;
  };
}

const ManageVenue = () => {
  const { dataId, sport, organizationId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const { data: sites = [], isLoading: isSitesLoading } = useFetchSites({ limit: 1000 }, true);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { data, isLoading: isFetchLoading } = useFetchVenue();
  const { createVenue, isCreatePending, updateVenue, isUpdatePending } = useVenueService();

  useEffect(() => {
    if (dataId) {
      reset({
        nameLocal: data?.nameLocal,
        nameLatin: data?.nameLatin,
        abbreviationLocal: data?.abbreviationLocal,
        abbreviationLatin: data?.abbreviationLatin,
        countryCode: data?.countryCode,
        timezone: data?.timezone || timeZones[0],
        status: data?.status || statuses[0],
        siteId: data?.siteId,
        capacity: data?.capacity,
        address: data?.address || {},
      });
    } else {
      reset({
        timezone: data?.timezone || timeZones[0],
        status: data?.status || statuses[0],
      });
    }
  }, [data, dataId, reset]);

  const title = useMemo(() => {
    return dataId
      ? `${formatMessage({
          id: "manage",
        })} ${data?.nameLocal ? `- ${data?.nameLocal}` : ""}`
      : `${formatMessage({
          id: "addVenue",
        })}`;
  }, [dataId, formatMessage, data?.nameLocal]);

  const onSubmit: SubmitHandler<IFormInput> = async (formData) => {
    if (dataId) {
      await updateVenue(dataId, formData);
      return;
    }

    await createVenue(formData, { redirectToList: true });
  };

  return (
    <Page title={title} tabTitle={title}>
      <LoaderContainer loading={isUpdatePending || isSitesLoading || isCreatePending || isFetchLoading}>
        <Content>
          <>
            <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
              <Row style={{ maxWidth: 960 }} gutter={16}>
                <Col span={12}>
                  <Row gutter={16}>
                    <Col span={16}>
                      <FormControl
                        labelText="name.local"
                        name="nameLocal"
                        labelTooltip={formatMessage(
                          {
                            id: "form.help.local.name.item",
                          },
                          {
                            item: formatMessage({
                              id: "venue",
                            }),
                          },
                        )}
                        rules={{
                          required: formatMessage(
                            {
                              id: "form.help.required.field",
                            },
                            {
                              field: formatMessage({
                                id: "name.local",
                              }),
                            },
                          ),
                          minLength: {
                            value: 2,
                            message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                          },
                          validate: {
                            noLeadingOrTrailingSpaces: noLeadingOrTrailingSpacesReactHookFormAdapter(formatMessage),
                          },
                        }}
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        errors={errors}
                      />
                    </Col>
                    <Col span={8}>
                      <FormControl
                        labelText="abbreviationLocal"
                        labelTooltip={formatMessage({
                          id: "form.help.local.abbreviation",
                        })}
                        name="abbreviationLocal"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                  </Row>
                  <FormControl
                    labelText="status"
                    name="status"
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "status",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    labelTooltip={formatMessage(
                      { id: "form.help.status.item" },
                      {
                        item: formatMessage({
                          id: "venue",
                        }),
                      },
                    )}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={statuses.map((status) => ({
                          value: status,
                          label: status,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="timezone"
                    name="timezone"
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "timezone",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    labelTooltip={formatMessage(
                      {
                        id: "form.help.timezone.of.the.item",
                      },
                      {
                        item: formatMessage({
                          id: "venue",
                        }),
                      },
                    )}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={timeZones.map((timeZone) => ({
                          value: timeZone,
                          label: timeZone,
                        }))}
                      />
                    )}
                    errors={errors}
                  />
                </Col>
                <Col span={12}>
                  <Row gutter={16}>
                    <Col span={16}>
                      <FormControl
                        labelText="name.latin"
                        name="nameLatin"
                        labelTooltip={formatMessage(
                          {
                            id: "form.help.latin.name.item",
                          },
                          {
                            item: formatMessage({
                              id: "venue",
                            }),
                          },
                        )}
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                    <Col span={8}>
                      <FormControl
                        labelText="abbreviationLatin"
                        labelTooltip={formatMessage({
                          id: "form.help.latin.abbreviation",
                        })}
                        name="abbreviationLatin"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                  </Row>
                  <FormControl
                    labelText="site"
                    name="siteId"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.venue.site",
                    })}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        {...field}
                        options={[
                          { value: null, label: "" },
                          ...sites.sort((a, b) => sortStringValues(a.nameLocal, b.nameLocal)).map((site: ISite) => ({
                            value: site.siteId,
                            label: site.nameLocal,
                          })),
                        ]}
                      />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="country"
                    name="countryCode"
                    control={control}
                    labelTooltip={formatMessage(
                      {
                        id: "form.help.country.of.the.item",
                      },
                      {
                        item: formatMessage({
                          id: "venue",
                        }),
                      },
                    )}
                    render={({ field }) => (
                      <SelectCountryCode
                        style={{ width: "100%", minWidth: "unset" }}
                        value={field.value}
                        onChange={(option) => field.onChange((option as LabelValue | undefined)?.value)}
                      />
                    )}
                    errors={errors}
                  />
                </Col>
                <Col span={24}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormControl
                        labelText="city"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.city",
                        })}
                        name="address.city"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="city"
                      />
                    </Col>
                    <Col span={12}>
                      <FormControl
                        labelText="state"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.state",
                        })}
                        name="address.state"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="state"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormControl
                        labelText="postalCode"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.postalCode",
                        })}
                        name="address.postalCode"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="postalCode"
                      />
                    </Col>
                    <Col span={12}>
                      <FormControl
                        labelText="address1"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.address",
                        })}
                        name="address.address1"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="address1"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormControl
                        labelText="address2"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.address",
                        })}
                        name="address.address2"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="address2"
                      />
                    </Col>
                    <Col span={12}>
                      <FormControl
                        labelText="address3"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.address",
                        })}
                        name="address.address3"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        labelTooltipId="address3"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormControl
                        labelText="latitude"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.latitude",
                        })}
                        name="address.latitude"
                        control={control}
                        rules={{
                          min: {
                            value: LATITUDE_MIN,
                            message: formatMessage(
                              { id: "form.validation.range" },
                              { min: LATITUDE_MIN, max: LATITUDE_MAX },
                            ),
                          },
                          max: {
                            value: LATITUDE_MAX,
                            message: formatMessage(
                              { id: "form.validation.range" },
                              { min: LATITUDE_MIN, max: LATITUDE_MAX },
                            ),
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormInputNumber
                            style={{ width: "100%" }}
                            {...field}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value ?? null)}
                          />
                        )}
                        labelTooltipId="latitude"
                        errors={errors}
                      />
                    </Col>
                    <Col span={12}>
                      <FormControl
                        labelText="longitude"
                        labelTooltip={formatMessage({
                          id: "form.help.venue.longitude",
                        })}
                        name="address.longitude"
                        control={control}
                        rules={{
                          min: {
                            value: LONGITUDE_MIN,
                            message: formatMessage(
                              { id: "form.validation.range" },
                              { min: LONGITUDE_MIN, max: LONGITUDE_MAX },
                            ),
                          },
                          max: {
                            value: LONGITUDE_MAX,
                            message: formatMessage(
                              { id: "form.validation.range" },
                              { min: LONGITUDE_MIN, max: LONGITUDE_MAX },
                            ),
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormInputNumber
                            style={{ width: "100%" }}
                            {...field}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value ?? null)}
                          />
                        )}
                        labelTooltipId="longitude"
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={5}>
                  <FormControl
                    labelText="capacity"
                    labelTooltip={formatMessage({
                      id: "form.help.venue.capacity",
                    })}
                    name="capacity"
                    control={control}
                    render={({ field }) => <StyledFormInputNumber style={{ width: "100%" }} {...field} />}
                  />
                </Col>
              </Row>
              <PermissionCheck
                permissionKeys={[PermissionName]}
                permissionOptions={dataId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
              >
                <Button htmlType="submit" type="primary">
                  {dataId ? formatMessage({ id: "venue.update" }) : formatMessage({ id: "venue.add" })}
                </Button>
              </PermissionCheck>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/venues`}>
                <Button style={{ marginLeft: 30, marginTop: 10 }} type="primary">
                  {formatMessage({ id: "close" })}
                </Button>
              </Link>
            </form>
          </>
          <RightColumn>{data?.venueId && <VenuesHistoricNames data={data} />}</RightColumn>
        </Content>
      </LoaderContainer>
    </Page>
  );
};

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 20px;
`;

const Content = styled.div`
  display: flex;

  .container {
    margin: 0;
  }
`;

export default ManageVenue;
